import Image from '@tiptap/extension-image';

// ─── Custom Image extension with drag-resize NodeView support ─────────────────
const CustomImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            src: {
                default: null,
                parseHTML: element => element.getAttribute('src'),
            },
            alt: {
                default: 'Embedded Image',
                parseHTML: element => element.getAttribute('alt') || 'Embedded Image',
            },
            title: {
                default: null,
                parseHTML: element => element.getAttribute('title'),
            },
            'data-width-percent': {
                default: '50',
                parseHTML: element => {
                    const dataAttr = element.getAttribute('data-width-percent');
                    if (dataAttr) return dataAttr;
                    const style = element.getAttribute('style') || '';
                    const widthMatch = style.match(/width:\s*(\d+)%/);
                    if (widthMatch) return widthMatch[1];
                    return '50';
                },
            },
            'data-height-pixels': {
                default: 'auto',
                parseHTML: element => {
                    const dataAttr = element.getAttribute('data-height-pixels');
                    if (dataAttr) return dataAttr;
                    const style = element.getAttribute('style') || '';
                    const heightMatch = style.match(/height:\s*(\d+)px/);
                    if (heightMatch) return heightMatch[1];
                    return 'auto';
                },
            },
            'data-align': {
                default: 'center',
                parseHTML: element => {
                    const dataAttr = element.getAttribute('data-align');
                    if (dataAttr) return dataAttr;
                    const className = element.getAttribute('class') || '';
                    if (className.includes('align-left')) return 'left';
                    if (className.includes('align-right')) return 'right';
                    if (className.includes('align-center')) return 'center';
                    return 'center';
                },
            },
        };
    },
    renderHTML({ HTMLAttributes }) {
        const widthPct = HTMLAttributes['data-width-percent'] || '50';
        const heightPx = HTMLAttributes['data-height-pixels'] || 'auto';
        const align = HTMLAttributes['data-align'] || 'center';
        const heightStyle = heightPx === 'auto' ? 'auto' : `${heightPx}px`;
        return ['img', {
            ...HTMLAttributes,
            alt: HTMLAttributes.alt || 'Embedded Image',
            class: `editor-embedded-image align-${align}`,
            style: `width:${widthPct}%;height:${heightStyle};`
        }];
    },
    addNodeView() {
        return ({ node, HTMLAttributes, getPos, editor }) => {
            let isNodeSelected = false;
            const container = document.createElement('div');
            const alignVal = node.attrs['data-align'] || 'center';
            const widthVal = node.attrs['data-width-percent'] || '50';
            const heightVal = node.attrs['data-height-pixels'] || 'auto';
            container.className = `image-resizer-container align-${alignVal}`;
            container.style.width = `${widthVal}%`;

            const img = document.createElement('img');
            img.src = node.attrs.src;
            img.alt = node.attrs.alt || 'Embedded Image';
            img.className = 'editor-embedded-image';
            img.style.width = '100%';
            img.style.height = heightVal === 'auto' ? 'auto' : `${heightVal}px`;
            img.style.display = 'block';
            container.appendChild(img);

            // Create resize handles
            const makeHandle = (className, styles) => {
                const h = document.createElement('div');
                h.className = `image-resize-handle ${className}`;
                Object.assign(h.style, { position: 'absolute', width: '8px', height: '8px', backgroundColor: '#FFFFFF', border: '1px solid #666666', borderRadius: '50%', cursor: 'pointer', zIndex: '5', display: 'none', ...styles });
                container.appendChild(h);
                return h;
            };

            const rightHandle = makeHandle('handle-right', { right: '-4px', top: '50%', transform: 'translateY(-50%)', cursor: 'e-resize' });
            const bottomHandle = makeHandle('handle-bottom', { left: '50%', bottom: '-4px', transform: 'translateX(-50%)', cursor: 's-resize' });
            const cornerHandle = makeHandle('handle-corner', { right: '-4px', bottom: '-4px', cursor: 'se-resize' });

            img.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof getPos === 'function') {
                    editor.chain().focus().setNodeSelection(getPos()).run();
                }
            });

            // ── Width drag (right handle) ──
            let isResizingWidth = false, startXWidth = 0, startWidthVal = 0, parentWidthVal = 0;
            rightHandle.addEventListener('mousedown', (e) => {
                e.preventDefault(); e.stopPropagation();
                isResizingWidth = true; startXWidth = e.clientX; startWidthVal = container.offsetWidth;
                parentWidthVal = container.parentElement ? container.parentElement.offsetWidth : editor.view.dom.offsetWidth;
                container.classList.add('is-resizing');
                const onMove = (e) => {
                    if (!isResizingWidth) return;
                    const newW = Math.max(10, Math.min(100, Math.round(((startWidthVal + e.clientX - startXWidth) / parentWidthVal) * 100)));
                    container.style.width = `${newW}%`;
                    const range = document.querySelector('.image-settings-panel input[type="range"]');
                    const num = document.querySelector('.image-settings-panel input[type="number"]');
                    if (range) range.value = newW;
                    if (num) num.value = newW;
                };
                const onUp = () => {
                    if (!isResizingWidth) return;
                    isResizingWidth = false;
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                    container.classList.remove('is-resizing');
                    const finalW = Math.max(10, Math.min(100, Math.round((container.offsetWidth / parentWidthVal) * 100)));
                    if (typeof getPos === 'function') {
                        editor.chain().focus().setNodeSelection(getPos()).updateAttributes('image', { 'data-width-percent': String(finalW), style: `width:${finalW}%;height:${node.attrs['data-height-pixels'] || 'auto'};` }).run();
                    }
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            });

            // ── Height drag (bottom handle) ──
            let isResizingHeight = false, startYHeight = 0, startHeightVal = 0;
            bottomHandle.addEventListener('mousedown', (e) => {
                e.preventDefault(); e.stopPropagation();
                isResizingHeight = true; startYHeight = e.clientY; startHeightVal = img.offsetHeight;
                container.classList.add('is-resizing');
                const onMove = (e) => {
                    if (!isResizingHeight) return;
                    img.style.height = `${Math.max(50, Math.min(1200, startHeightVal + e.clientY - startYHeight))}px`;
                };
                const onUp = () => {
                    if (!isResizingHeight) return;
                    isResizingHeight = false;
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                    container.classList.remove('is-resizing');
                    const finalH = img.offsetHeight;
                    if (typeof getPos === 'function') {
                        editor.chain().focus().setNodeSelection(getPos()).updateAttributes('image', { 'data-height-pixels': String(finalH), style: `width:${node.attrs['data-width-percent'] || '50'}%;height:${finalH}px;` }).run();
                    }
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            });

            // ── Both drag (corner handle) ──
            let isResizingBoth = false, startXBoth = 0, startYBoth = 0, startWidthBoth = 0, startHeightBoth = 0, parentWidthBoth = 0;
            cornerHandle.addEventListener('mousedown', (e) => {
                e.preventDefault(); e.stopPropagation();
                isResizingBoth = true; startXBoth = e.clientX; startYBoth = e.clientY;
                startWidthBoth = container.offsetWidth; startHeightBoth = img.offsetHeight;
                parentWidthBoth = container.parentElement ? container.parentElement.offsetWidth : editor.view.dom.offsetWidth;
                container.classList.add('is-resizing');
                const onMove = (e) => {
                    if (!isResizingBoth) return;
                    const newW = Math.max(10, Math.min(100, Math.round(((startWidthBoth + e.clientX - startXBoth) / parentWidthBoth) * 100)));
                    container.style.width = `${newW}%`;
                    img.style.height = `${Math.max(50, Math.min(1200, startHeightBoth + e.clientY - startYBoth))}px`;
                    const range = document.querySelector('.image-settings-panel input[type="range"]');
                    const num = document.querySelector('.image-settings-panel input[type="number"]');
                    if (range) range.value = newW;
                    if (num) num.value = newW;
                };
                const onUp = () => {
                    if (!isResizingBoth) return;
                    isResizingBoth = false;
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                    container.classList.remove('is-resizing');
                    const finalW = Math.max(10, Math.min(100, Math.round((container.offsetWidth / parentWidthBoth) * 100)));
                    const finalH = img.offsetHeight;
                    if (typeof getPos === 'function') {
                        editor.chain().focus().setNodeSelection(getPos()).updateAttributes('image', { 'data-width-percent': String(finalW), 'data-height-pixels': String(finalH), style: `width:${finalW}%;height:${finalH}px;` }).run();
                    }
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            });

            cornerHandle.addEventListener('dblclick', (e) => {
                e.preventDefault(); e.stopPropagation();
                if (typeof getPos === 'function') {
                    editor.chain().focus().setNodeSelection(getPos()).updateAttributes('image', { 'data-width-percent': '50', 'data-height-pixels': 'auto', style: `width:50%;height:auto;` }).run();
                }
            });

            const showHandles = (show) => {
                [rightHandle, bottomHandle, cornerHandle].forEach(h => h.style.display = show ? 'block' : 'none');
            };

            return {
                dom: container,
                contentDOM: null,
                update: (updatedNode) => {
                    if (updatedNode.type.name !== node.type.name) return false;
                    container.style.width = `${updatedNode.attrs['data-width-percent'] || '50'}%`;
                    img.style.height = (updatedNode.attrs['data-height-pixels'] || 'auto') === 'auto' ? 'auto' : `${updatedNode.attrs['data-height-pixels']}px`;
                    container.className = `image-resizer-container align-${updatedNode.attrs['data-align'] || 'center'}`;
                    if (isNodeSelected) { container.classList.add('is-selected'); showHandles(true); }
                    else { container.classList.remove('is-selected'); showHandles(false); }
                    return true;
                },
                selectNode() { isNodeSelected = true; container.classList.add('is-selected'); showHandles(true); },
                deselectNode() { isNodeSelected = false; container.classList.remove('is-selected'); showHandles(false); }
            };
        };
    }
});

export default CustomImage;
